import { BookOpen, Search, X } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { MEAL_NAMES, MEALS } from '../../../shared/meals'
import { FormError } from '@/components/FormError'
import { KitchenStory } from '@/components/recipes/KitchenStory'
import { RecipeWall } from '@/components/recipes/RecipeWall'
import { Avatar } from '@/components/tree/Avatar'
import type { Family, Space } from '@/lib/families'
import { kitchens, narrow, type Chip, type Kitchen } from '@/lib/modernRecipes'
import { photoUrl } from '@/lib/photos'
import { useAllRecipes, useBooks } from '@/lib/recipes'
import { cn } from '@/lib/utils'

// Modern Recipes (the blend): kitchens across the top in rings, then the books; chips and an ingredient box to narrow
// the wall; then every recipe as a whole dish that turns over to its card. A kitchen's ring is drawn in the logo's
// colours while it has a recipe added since you last looked (seenAt), and grey once you've seen them all. Tapping a
// kitchen plays its cards one after another; tapping a book opens it.
export function ModernRecipes({
  family,
  space,
  mine,
  seenAt,
}: {
  family: Family
  space: Space | null
  mine: boolean
  // When you last looked at Recipes in this circle (null: never, so nothing is marked new).
  seenAt: number | null
}) {
  const recipes = useAllRecipes(family.id, mine, space?.id)
  const books = useBooks(family.id, space?.id)
  const all = useMemo(() => recipes.data?.recipes ?? [], [recipes.data])
  const kitchenList = useMemo(() => kitchens(all), [all])
  const [chip, setChip] = useState<Chip>(null)
  const [typed, setTyped] = useState('')
  const [playing, setPlaying] = useState<Kitchen | null>(null)
  const shown = useMemo(() => narrow(all, chip, typed), [all, chip, typed])
  const meals = MEALS.filter((m) => all.some((x) => x.meals.includes(m)))
  const bookList = (books.data?.books ?? []).filter((b) => b.recipes > 0)
  const isNew = (k: Kitchen) => seenAt !== null && k.newest > seenAt
  const same = (a: Chip, b: Chip) => JSON.stringify(a) === JSON.stringify(b)
  const toggle = (next: Chip) => setChip((now) => (same(now, next) ? null : next))

  return (
    <div className="flex flex-col gap-6 nav:gap-8">
      <FormError error={recipes.error ?? books.error} />
      {(kitchenList.length > 0 || bookList.length > 0) && (
        <ul aria-label="Kitchens and books" className="-mx-4 scroll-row gap-3.5 px-4 pt-1 pb-1 nav:-mx-2 nav:px-2">
          {kitchenList.map((k) => (
            <li key={k.person.id} className="snap-start">
              <Ring
                label={k.person.name}
                fresh={isNew(k)}
                onClick={() => setPlaying(k)}
                hint={`Play ${k.person.name}’s cards${isNew(k) ? ', with something new' : ''}`}
              >
                <Avatar person={k.person} className="size-full text-xl shadow-none" />
              </Ring>
            </li>
          ))}
          {bookList.map((b) => {
            const cover = b.covers?.[0] ?? b.cover
            return (
              <li key={b.id} className="snap-start">
                <Ring label={b.title} to={`/book/${b.id}`} hint={`Open ${b.title}`}>
                  {cover ? (
                    <img src={photoUrl(cover, 'thumb')} alt="" className="size-full object-cover" />
                  ) : (
                    <BookOpen className="size-6 text-muted-foreground" aria-hidden />
                  )}
                </Ring>
              </li>
            )
          })}
        </ul>
      )}

      {all.length > 0 && (
        <div
          role="group"
          aria-label="Narrow the recipes"
          className="-mx-4 scroll-row gap-2 px-4 nav:mx-0 nav:flex-wrap nav:px-0"
        >
          <label className="relative flex shrink-0 items-center">
            <Search className="pointer-events-none absolute left-3.5 size-4 text-muted-foreground" aria-hidden />
            <input
              type="search"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Ingredient"
              aria-label="Find recipes that use an ingredient"
              className="h-10 w-40 rounded-full border bg-background pr-3 pl-9 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 nav:w-48"
            />
          </label>
          <ChipButton on={chip === null} onClick={() => setChip(null)}>
            All
          </ChipButton>
          {meals.map((meal) => (
            <ChipButton
              key={meal}
              on={same(chip, { kind: 'meal', meal })}
              onClick={() => toggle({ kind: 'meal', meal })}
            >
              {MEAL_NAMES[meal].one}
            </ChipButton>
          ))}
          {kitchenList.map((k) => (
            <ChipButton
              key={k.person.id}
              on={same(chip, { kind: 'from', id: k.person.id })}
              onClick={() => toggle({ kind: 'from', id: k.person.id })}
            >
              <Avatar person={k.person} className="-ml-1.5 size-6 text-[0.625rem] shadow-none" />
              {k.person.name}
            </ChipButton>
          ))}
          {bookList.map((b) => (
            <ChipButton
              key={b.id}
              on={same(chip, { kind: 'book', id: b.id })}
              onClick={() => toggle({ kind: 'book', id: b.id })}
            >
              {b.title}
            </ChipButton>
          ))}
        </div>
      )}

      {recipes.data && all.length === 0 && (
        <p className="text-muted-foreground">
          {mine
            ? 'You haven’t added any recipes yet.'
            : 'Every circle has a recipe worth keeping. Photograph the first card.'}
        </p>
      )}
      {all.length > 0 && shown.length === 0 && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          No recipes {typed.trim() ? `use “${typed.trim()}”` : 'here'}
          {chip ? ' with that tag' : ''}.
          <button
            type="button"
            onClick={() => {
              setChip(null)
              setTyped('')
            }}
            className="inline-flex min-h-11 items-center gap-1 font-medium text-foreground underline-offset-4 hover:underline"
          >
            <X className="size-4" aria-hidden /> Clear
          </button>
        </p>
      )}
      {shown.length > 0 && <RecipeWall recipes={shown} typed={typed} />}
      {playing && <KitchenStory kitchen={playing} onClose={() => setPlaying(null)} />}
    </div>
  )
}

export function Ring({
  label,
  hint,
  fresh,
  to,
  onClick,
  children,
}: {
  label: string
  hint: string
  fresh?: boolean
  to?: string
  onClick?: () => void
  children: ReactNode
}) {
  const body = (
    <>
      <span
        className={cn(
          'grid size-[4.25rem] place-items-center rounded-full p-[3px] transition-transform duration-300 ease-settle group-active:scale-95',
          fresh
            ? 'bg-[conic-gradient(from_210deg,var(--logo-1),var(--logo-2),var(--logo-3),var(--logo-4),var(--logo-1))]'
            : 'bg-border',
        )}
      >
        <span className="grid size-full place-items-center overflow-hidden rounded-full border-[3px] border-background bg-secondary">
          {children}
        </span>
      </span>
      <span className="w-[4.5rem] truncate text-center text-xs">{label}</span>
    </>
  )
  const className =
    'group flex flex-col items-center gap-1.5 rounded-2xl p-0.5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
  return to ? (
    <Link to={to} viewTransition aria-label={hint} className={className}>
      {body}
    </Link>
  ) : (
    <button type="button" aria-label={hint} onClick={onClick} className={className}>
      {body}
    </button>
  )
}

export function ChipButton({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        on ? 'bg-brand text-brand-foreground' : 'bg-secondary text-foreground hover:bg-accent',
      )}
    >
      {children}
    </button>
  )
}
